import React, { useCallback, useEffect, useState } from "react";
import ReactDOM from "react-dom";
import {
  CircularProgress,
  Radio,
  TextField,
  ThemeProvider,
} from "@material-ui/core";
import Checkbox from "@material-ui/core/Checkbox";
import {
  AutocompleteChangeReason,
  AutocompleteInputChangeReason,
} from "@material-ui/lab/Autocomplete";
import { useGroopitApiContext } from "groopit-js";
import { AutocompleteChangeDetails } from "@material-ui/lab/useAutocomplete/useAutocomplete";
import { useDebounce } from "react-use";
import { groopTheme, StyledAutoComplete } from "../styles/MaterialGroopTheme";
import { OverflowTooltip } from "../Controls/OverflowTooltip";

interface MultipleSelectionExtendedComponentSelectorProps {
  assignmentId: string;
  baseName: string;
  choice?: string;
  componentModelId: number;
  description: string;
  multipleSelect: boolean;
}

const limitValue = 100;
const groopToken = undefined;

export const MultipleChoiceExtendedComponentSelector = (
  props: MultipleSelectionExtendedComponentSelectorProps,
): JSX.Element => {
  const { baseName, description } = { ...props };

  const [initialOptionsLoaded, setInitialOptionsLoaded] = useState(false);
  const [allowMultipleSelection] = useState(props.multipleSelect);
  const [availableOptions, setAvailableOptions] = useState<Set<string>>(
    new Set(),
  );

  const [selectedOptions, setSelectedOptions] = useState<Set<string>>(
    new Set(),
  );

  const [cachedOriginalAvailableOptions, setCachedOriginalAvailableOptions] =
    useState<Set<string>>(new Set());

  const [assignmentId] = useState(props.assignmentId);
  const [componentModelId] = useState(props.componentModelId);

  const [query, setQuery] = useState<string>("");

  const [loading, setLoading] = useState(false);

  const [assignmentApiClient] = useState(useGroopitApiContext().assignment);

  const loadOptions = useCallback(
    async (query: string | undefined) => {
      setLoading(true);

      try {
        return await assignmentApiClient.getComponentOptions(
          assignmentId,
          componentModelId,
          query,
          groopToken,
          limitValue,
        );
      } finally {
        setLoading(false);
      }
    },
    [assignmentApiClient, assignmentId, componentModelId],
  );

  const loadOptionsOnPageLoad = useCallback(() => {
    const query = undefined;

    loadOptions(query).then((optionsResponse: string[]) => {
      setAvailableOptions(new Set<string>(optionsResponse));
      setCachedOriginalAvailableOptions(new Set<string>(optionsResponse));
      setInitialOptionsLoaded(true);
    });
  }, [loadOptions]);

  const loadOptionsOnQueryChange = useCallback(
    (query: string) => {
      setLoading(true);

      if (query === "") {
        // The user has manually cleared the search by deleting the characters in the input.
        setAvailableOptions(
          new Set<string>(
            Array.from(cachedOriginalAvailableOptions).concat(
              Array.from(selectedOptions),
            ),
          ),
        );
        setLoading(false);
        return;
      }

      loadOptions(query).then((optionsResponse: string[]) => {
        setAvailableOptions(
          new Set<string>(optionsResponse.concat(Array.from(selectedOptions))),
        );
      });
    },
    [loadOptions, cachedOriginalAvailableOptions, selectedOptions],
  );

  const setSelectedOptionsOnLoad = useCallback(() => {
    let parsedChoice: string;

    if (props.choice) {
      try {
        const jsonParsedChoice = JSON.parse(props.choice);

        if (
          typeof jsonParsedChoice === "number" &&
          isFinite(jsonParsedChoice)
        ) {
          parsedChoice = jsonParsedChoice.toString();
        } else {
          parsedChoice = jsonParsedChoice;
        }
      } catch {
        parsedChoice = props.choice;
      }
    } else {
      parsedChoice = "";
    }

    setSelectedOptions(new Set<string>(parsedChoice));
  }, [props.choice]);

  useEffect(() => {
    loadOptionsOnPageLoad();
    setSelectedOptionsOnLoad();
  }, [loadOptionsOnPageLoad, setSelectedOptionsOnLoad]);

  useDebounce(
    () => {
      loadOptionsOnQueryChange(query);
    },
    500,
    [loadOptionsOnQueryChange, query],
  );

  const inputChanged = (
    event: React.ChangeEvent<object>,
    value: string,
    reason: AutocompleteInputChangeReason,
  ) => {
    if (event?.target === undefined || reason === "reset") {
      return;
    }

    setQuery(value);
  };

  const multipleSelectSelectedOptionChanged = (
    _event: React.ChangeEvent<object>,
    options: string[] | null,
    changeReason: AutocompleteChangeReason,
  ) => {
    if (changeReason === "clear") {
      setSelectedOptions(new Set<string>());
      setAvailableOptions(
        new Set<string>(Array.from(cachedOriginalAvailableOptions)),
      );
      return;
    }

    if (changeReason === "select-option") {
      setSelectedOptions(new Set<string>(options));
      setAvailableOptions(
        new Set<string>(
          Array.from(availableOptions).concat(Array.from(selectedOptions)),
        ),
      );
    } else if (changeReason === "remove-option") {
      setSelectedOptions(new Set<string>(options));
    }
    setQuery("");
  };

  const singleSelectSelectedOptionChanged = (
    _event: React.ChangeEvent<object>,
    _options: string[] | null,
    changeReason: AutocompleteChangeReason,
    details?: AutocompleteChangeDetails,
  ) => {
    const option = details?.option;

    if (changeReason === "clear") {
      setSelectedOptions(new Set<string>());
      setAvailableOptions(
        new Set<string>(Array.from(cachedOriginalAvailableOptions)),
      );
      return;
    }

    if (changeReason === "select-option" && option) {
      setSelectedOptions(new Set<string>([option]));
    } else if (changeReason === "remove-option" && option) {
      const updatedSelectedOptions = new Set<string>(selectedOptions);
      updatedSelectedOptions.delete(option);
      setSelectedOptions(updatedSelectedOptions);
    }
    setQuery("");
  };

  return (
    <div className="flex space-x-3 post-component">
      <img
        src="/Content/Images/ic_list.png"
        className="groopit-component-icon-sm"
      />
      <div className="flex-grow">
        <div className="source-custom">
          <input
            name={`${baseName}.Choice`}
            type="hidden"
            value={JSON.stringify(Array.from(selectedOptions))}
          />

          <input
            name={`${baseName}.Description`}
            type="hidden"
            value={props.description}
          />

          <input
            name={`${baseName}.AssignmentId`}
            type="hidden"
            value={assignmentId}
          />

          <input
            name={`${baseName}.MultipleSelect`}
            type="hidden"
            value={allowMultipleSelection.toString()}
          />

          {initialOptionsLoaded && (
            <div className="grid grid-cols-1">
              <label className="text-display-normal text-color-secondary">
                {description}
                <StyledAutoComplete
                  className="mt-2"
                  multiple
                  limitTags={1}
                  freeSolo
                  loading={loading}
                  disableCloseOnSelect={allowMultipleSelection}
                  forcePopupIcon={true}
                  clearOnBlur={false}
                  filterOptions={(options) => options}
                  options={Array.from(availableOptions)}
                  inputValue={query}
                  onInputChange={inputChanged}
                  renderTags={(value) => (
                    <span className="ml-1 truncate w-full">
                      {allowMultipleSelection ? (
                        Array.from(selectedOptions.keys()).length == 1 ? (
                          <div className="truncate">
                            {Array.from(selectedOptions.keys())[0]}
                          </div>
                        ) : (
                          `${
                            Array.from(selectedOptions.keys()).length
                          } selected`
                        )
                      ) : (
                        value
                      )}
                    </span>
                  )}
                  value={Array.from(selectedOptions.keys())}
                  onChange={(e, options, changeReason, details) =>
                    allowMultipleSelection
                      ? multipleSelectSelectedOptionChanged(
                          e,
                          options,
                          changeReason,
                        )
                      : singleSelectSelectedOptionChanged(
                          e,
                          options,
                          changeReason,
                          details,
                        )
                  }
                  renderOption={(option, state) => (
                    <React.Fragment>
                      {allowMultipleSelection ? (
                        <Checkbox
                          checked={state.selected}
                          className="text-app-teal"
                        />
                      ) : (
                        <Radio
                          checked={state.selected}
                          className="text-app-teal"
                        />
                      )}
                      <OverflowTooltip text={option} className="text-sm" />
                    </React.Fragment>
                  )}
                  renderInput={(params) => (
                    <>
                      <div></div>
                      <TextField
                        {...params}
                        label={
                          allowMultipleSelection
                            ? "Select multiple"
                            : "Select one"
                        }
                        variant="outlined"
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <React.Fragment>
                              {loading ? (
                                <CircularProgress color="inherit" size={20} />
                              ) : null}
                              {params.InputProps.endAdornment}
                            </React.Fragment>
                          ),
                        }}
                      />
                    </>
                  )}
                />
              </label>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export const initializeMultipleChoiceExtendedComponentSelector = (
  element: HTMLElement,
) => {
  ReactDOM.render(
    <ThemeProvider theme={groopTheme}>
      <MultipleChoiceExtendedComponentSelector
        assignmentId={element.dataset.assignmentId as string}
        baseName={element.dataset.baseName as string}
        choice={element.dataset.choice as string}
        componentModelId={element.dataset.componentModelId as unknown as number}
        description={element.dataset.description as string}
        multipleSelect={element.dataset.multiselect === "True"}
      />
    </ThemeProvider>,
    element,
  );
};
